import React, { useState } from 'react';
import {
  X,
  Mail,
  Send,
  Eye,
  Users,
  Target,
  FileText,
  Edit,
  Search,
  Sparkles,
  Building2,
  Plus,
  Trash2,
  Copy,
  Check
} from 'lucide-react';

const BulkEmailModal = ({ 
  candidates, 
  currentUser,
  onClose, 
  useToast, 
  sendEmail, 
  API_BASE_URL 
}) => {
  // Extract toast functions
  const { success, error, warning } = useToast();

  // State for bulk email functionality
  const [recipientType, setRecipientType] = useState('candidates'); // New state for tab selection
  const [bulkEmailMode, setBulkEmailMode] = useState('template');
  const [selectedTemplate, setSelectedTemplate] = useState('');
  const [bulkSubject, setBulkSubject] = useState('');
  const [bulkMessage, setBulkMessage] = useState('');
  const [bulkSelectedCandidates, setBulkSelectedCandidates] = useState([]);
  const [bulkSearchTerm, setBulkSearchTerm] = useState('');
  const [bulkFilterBy, setBulkFilterBy] = useState('all');
  const [bulkPreviewMode, setBulkPreviewMode] = useState(false);
  const [bulkSending, setBulkSending] = useState(false);
  const [bulkProgress, setBulkProgress] = useState(0);
  const [copiedTemplate, setCopiedTemplate] = useState(null);
  
  // Client email state
  const [clientEmails, setClientEmails] = useState(['']);

  // Candidate templates
  const candidateTemplates = [
    {
      id: 'work_info_request',
      name: 'Work Information Request',
      description: 'Request work email and manager details',
      subject: 'Work Information Request - {{candidateName}}',
      category: 'Information',
      icon: '📋',
      priority: 'high',
      content: `Hope you're doing well and settling in confidently with your current assignment.

As part of our efforts to strengthen support throughout the duration of your project, we're in the process of aligning key information related to your engagement. Having your **work email** and **reporting manager's contact details** on file helps us:

Ensure project updates or extension discussions are routed efficiently
Act swiftly in case of any support requests, clarifications, or compliance checks
Maintain transparency and alignment between you, the client, and our internal teams

These details are strictly used for internal coordination and help us stay responsive and proactive on your behalf.

When you get a moment, please share the following:
Your **official work email address**
Your **reporting manager's name**, **email**, and **phone number**

Thank you for your time and partnership—we truly appreciate your collaboration in keeping everything streamlined.

Best regards,`
    },
    {
      id: 'extension_inquiry',
      name: 'Extension Inquiry',
      description: 'Ask about potential assignment extension',
      subject: 'Extension Discussion Update - {{candidateName}}',
      category: 'Extension',
      icon: '🔄',
      priority: 'medium',
      content: `I hope this message finds you well. I am reaching out to inquire whether there have been any discussions or updates from your team or from your Manager regarding a potential extension of your current assignment with {{client}}.

As we approach the conclusion of the existing engagement period, our objective is to proactively coordinate resources and ensure alignment between all stakeholders. Understanding your current position on this matter will significantly aid our planning efforts.

Should you have any insights, preliminary updates, or formal communications to share regarding an extension, I would greatly appreciate your input. Additionally, if there are any immediate requirements or support needed from our end to facilitate this process, please do not hesitate to contact me directly.

Thank you for your attention to this matter. I look forward to your update and remain available to address any questions or concerns you may have.

Best regards,`
    }
  ];

  // Client templates
  const clientTemplates = [
    {
      id: 'extension_check',
      name: 'Extension Possibility Check',
      description: 'Check with client about potential extension',
      subject: 'Update Request: Extension Possibility for {{candidateName}}',
      category: 'Extension',
      icon: '🔄',
      priority: 'high',
      content: `Hi [Client Name],

I hope everything is going well on your end.

I wanted to check in regarding {{candidateName}}'s current assignment with your team. As we near the end of the engagement, I wanted to see if there are any discussions or decisions underway about a possible extension.

Having visibility on the next steps will help us coordinate effectively and ensure seamless support for both your team and the candidate.

Appreciate any updates you can share. Please feel free to reach out if there's anything we can assist with.

Looking forward to your response.

Best regards,`
    },
    {
      id: 'performance_feedback',
      name: 'Performance Feedback Request',
      description: 'Request feedback on candidate performance',
      subject: 'Request for Feedback on Candidate Performance - {{candidateName}}',
      category: 'Feedback',
      icon: '💭',
      priority: 'medium',
      content: `Hi [Client Name],

I hope you're doing well.

I wanted to check in with you regarding {{candidateName}}, who has been working with your team on the [Project/Role Name] since {{startDate}}. We value your input and would greatly appreciate it if you could share your feedback on their performance so far.

Specifically, it would be helpful to know:
• How the candidate is adapting to the role
• Their communication and collaboration with the team
• Any areas of strength or improvement you've noticed
• Whether there are any concerns or additional support required

Your insights will help us ensure continued alignment and support both for your team and the candidate.

Looking forward to hearing from you.

Best regards,`
    }
  ];

  // Get current templates based on recipient type
  const getCurrentTemplates = () => {
    return recipientType === 'candidates' ? candidateTemplates : clientTemplates;
  };

  // Copy template function
  const copyTemplate = async (template) => {
    try {
      const textToCopy = `Subject: ${template.subject}\n\n${template.content}`;
      await navigator.clipboard.writeText(textToCopy);
      setCopiedTemplate(template.id);
      success('Template copied to clipboard!');
      setTimeout(() => setCopiedTemplate(null), 2000);
    } catch (err) {
      error('Failed to copy template');
    }
  };

  // Client email management functions
  const addClientEmail = () => {
    setClientEmails(prev => [...prev, '']);
  };

  const removeClientEmail = (index) => {
    if (clientEmails.length > 1) {
      setClientEmails(prev => prev.filter((_, i) => i !== index));
    }
  };

  const updateClientEmail = (index, value) => {
    setClientEmails(prev => {
      const updated = [...prev];
      updated[index] = value;
      return updated;
    });
  };

  // Reset form when recipient type changes
  const handleRecipientTypeChange = (newType) => {
    setRecipientType(newType);
    setBulkSelectedCandidates([]);
    setClientEmails(['']);
    setSelectedTemplate('');
    setBulkSubject('');
    setBulkMessage('');
    setBulkPreviewMode(false);
  };

  // Filter candidates based on search and filter criteria
  const getFilteredCandidatesForBulk = () => {
    let filtered = candidates;
    
    // Apply search filter
    if (bulkSearchTerm) {
      filtered = filtered.filter(candidate =>
        candidate.candidateName.toLowerCase().includes(bulkSearchTerm.toLowerCase()) ||
        candidate.candidateEmail.toLowerCase().includes(bulkSearchTerm.toLowerCase()) ||
        candidate.client.toLowerCase().includes(bulkSearchTerm.toLowerCase())
      );
    }
    
    // Apply category filter
    switch (bulkFilterBy) {
      case 'active':
        filtered = filtered.filter(candidate => candidate.status === 'active');
        break;
      case 'ending_soon':
        filtered = filtered.filter(candidate => {
          if (!candidate.endDate) return false;
          const endDate = new Date(candidate.endDate);
          const today = new Date();
          const diffTime = endDate - today;
          const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
          return diffDays <= 30 && diffDays > 0;
        });
        break;
      case 'recent':
        filtered = filtered.filter(candidate => {
          if (!candidate.created_at) return false;
          const createdDate = new Date(candidate.created_at);
          const thirtyDaysAgo = new Date();
          thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
          return createdDate >= thirtyDaysAgo;
        });
        break;
      default:
        // 'all' - no additional filtering
        break;
    }
    
    return filtered;
  };

  // Toggle candidate selection for bulk email
  const toggleCandidateSelection = (candidateId) => {
    setBulkSelectedCandidates(prev => {
      if (prev.includes(candidateId)) {
        return prev.filter(id => id !== candidateId);
      } else {
        return [...prev, candidateId];
      }
    });
  };

  // Select all filtered candidates
  const selectAllFilteredCandidates = () => {
    const filtered = getFilteredCandidatesForBulk();
    const allIds = filtered.map(c => c.id);
    setBulkSelectedCandidates(allIds);
  };

  // Clear all selections
  const clearAllSelections = () => {
    setBulkSelectedCandidates([]);
  };

  // Get recipient count based on current type
  const getRecipientCount = () => {
    if (recipientType === 'candidates') {
      return bulkSelectedCandidates.length;
    } else {
      return clientEmails.filter(email => email.trim()).length;
    }
  };

  // Template variable replacement
  const replaceTemplateVariables = (text, candidate = null) => {
    let processed = text;
    
    // Common replacements
    processed = processed.replace(/\{\{date\}\}/g, new Date().toLocaleDateString());
    
    // Candidate-specific replacements
    if (candidate) {
      processed = processed
        .replace(/\{\{candidateName\}\}/g, candidate.candidateName || '')
        .replace(/\{\{client\}\}/g, candidate.client || '')
        .replace(/\{\{jobTitle\}\}/g, candidate.jobTitle || '')
        .replace(/\{\{startDate\}\}/g, candidate.startDate || '')
        .replace(/\{\{projectCity\}\}/g, candidate.projectCity || '')
        .replace(/\{\{projectState\}\}/g, candidate.projectState || '')
        .replace(/\{\{endDate\}\}/g, candidate.endDate || '');
    }
    
    return processed;
  };

  // Send bulk emails
  const sendBulkEmails = async () => {
  try {
    setBulkSending(true);
    setBulkProgress(0);
    
    // Handle client emails separately - SEND AS SINGLE REQUEST
    if (recipientType === 'clients') {
      const validEmails = clientEmails.filter(email => email.trim() !== '');
      
      if (validEmails.length === 0) {
        error('Please enter at least one valid client email address');
        setBulkSending(false);
        return;
      }

      // ADD THESE LINES - Get sender info
      const senderEmail = currentUser?.email || 'system@vdartinc.com';
      const senderName = currentUser ? `${currentUser.first_name} ${currentUser.last_name}` : 'System Admin';

      let emailData = {};
      
      if (bulkEmailMode === 'template' && selectedTemplate) {
        const template = getCurrentTemplates().find(t => t.id === selectedTemplate);
        emailData = {
          emailType: 'custom',
          customSubject: replaceTemplateVariables(template.subject),
          customMessage: replaceTemplateVariables(template.content),
          recipients: validEmails.join(','), // Send all emails as comma-separated string
          recipientType: 'client',
          senderEmail,      // ADD THIS
          senderName,       // ADD THIS
          sentBy: currentUser?.id  // ADD THIS
        };
      } else if (bulkEmailMode === 'custom') {
        emailData = {
          emailType: 'custom',
          customSubject: replaceTemplateVariables(bulkSubject),
          customMessage: replaceTemplateVariables(bulkMessage),
          recipients: validEmails.join(','), // Send all emails as comma-separated string
          recipientType: 'client',
          senderEmail,      // ADD THIS
          senderName,       // ADD THIS
          sentBy: currentUser?.id  // ADD THIS
        };
      }

      try {
        const response = await fetch(`${API_BASE_URL}/send_email.php`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(emailData)
        });
        
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        
        const result = await response.json();
        
        if (result.success) {
          success(`🎉 Email sent successfully to ${validEmails.length} client(s)!`);
          // Reset form
          setClientEmails(['']);
          setBulkSubject('');
          setBulkMessage('');
          setSelectedTemplate('');
          onClose();
        } else {
          throw new Error(result.message || 'Failed to send emails');
        }
        
      } catch (emailError) {
        console.error('Client email error:', emailError);
        error(`Failed to send client emails: ${emailError.message}`);
      }
      
      setBulkSending(false);
      return; // Exit early for client emails
    }
    
    // EXISTING CODE FOR CANDIDATES (keep as is)
    const selectedCands = candidates.filter(c => bulkSelectedCandidates.includes(c.id));
    let emailTargets = selectedCands.map(candidate => ({ 
      type: 'candidate', 
      data: candidate, 
      email: candidate.candidateEmail 
    }));

    // ADD THESE LINES - Get sender info for candidates
    const senderEmail = currentUser?.email || 'system@vdartinc.com';
    const senderName = currentUser ? `${currentUser.first_name} ${currentUser.last_name}` : 'System Admin';
    
    let successCount = 0;
    let failCount = 0;
    const errors = [];
    
    for (let i = 0; i < emailTargets.length; i++) {
      const target = emailTargets[i];
      
      try {
        let emailData = {};
        
        if (bulkEmailMode === 'template' && selectedTemplate) {
          const template = getCurrentTemplates().find(t => t.id === selectedTemplate);
          const processedSubject = replaceTemplateVariables(template.subject, target.data);
          const processedContent = replaceTemplateVariables(template.content, target.data);
          
          emailData = {
            emailType: 'custom',
            candidateId: target.data.id,
            customSubject: processedSubject,
            customMessage: processedContent,
            recipients: target.email,
            recipientType: 'candidate',
            senderEmail,        // ADD THIS
            senderName,         // ADD THIS
            sentBy: currentUser?.id  // ADD THIS
          };
        } else if (bulkEmailMode === 'custom') {
          const processedSubject = replaceTemplateVariables(bulkSubject, target.data);
          const processedMessage = replaceTemplateVariables(bulkMessage, target.data);
          
          emailData = {
            emailType: 'custom',
            candidateId: target.data.id,
            customSubject: processedSubject,
            customMessage: processedMessage,
            recipients: target.email,
            recipientType: 'candidate',
            senderEmail,        // ADD THIS
            senderName,         // ADD THIS
            sentBy: currentUser?.id  // ADD THIS
          };
        }
        
        const response = await fetch(`${API_BASE_URL}/send_email.php`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(emailData)
        });
        
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        
        const result = await response.json();
        
        if (result.success) {
          successCount++;
        } else {
          failCount++;
          const recipientName = target.data.candidateName;
          errors.push(`${recipientName}: ${result.message || 'Unknown error'}`);
          console.error(`Failed to send email to ${recipientName}:`, result.message);
        }
        
      } catch (emailError) {
        failCount++;
        const recipientName = target.data.candidateName;
        errors.push(`${recipientName}: ${emailError.message}`);
        console.error(`Email error for ${recipientName}:`, emailError);
      }
      
      // Update progress
      setBulkProgress(((i + 1) / emailTargets.length) * 100);
      
      // Small delay to prevent overwhelming the server
      await new Promise(resolve => setTimeout(resolve, 500));
    }
    
    // Show results using your toast functions
    if (successCount > 0 && failCount === 0) {
      success(`🎉 All ${successCount} emails sent successfully!`);
    } else if (successCount > 0 && failCount > 0) {
      warning(`Partially successful: ${successCount} sent, ${failCount} failed. Check console for details.`);
      console.warn('Email failures:', errors);
    } else {
      error(`Failed to send all emails. ${failCount} emails failed.`);
      console.error('Email failures:', errors);
    }
    
    // Reset form
    if (successCount > 0) {
      setBulkSelectedCandidates([]);
      setClientEmails(['']);
      setBulkSubject('');
      setBulkMessage('');
      setSelectedTemplate('');
      onClose();
    }
    
  } catch (error) {
    console.error('Bulk email error:', error);
    error('Failed to send bulk emails: ' + error.message);
  } finally {
    setBulkSending(false);
    setBulkProgress(0);
  }
};

  const generatePreviewContent = () => {
    let subject = '';
    let content = '';
    let previewFor = '';

    if (recipientType === 'candidates') {
      if (bulkSelectedCandidates.length === 0) {
        return {
          subject: 'No recipients selected',
          content: 'Please select at least one candidate to preview the email.',
          previewFor: 'No selection'
        };
      }

      const firstCandidate = candidates.find(c => bulkSelectedCandidates.includes(c.id));
      if (!firstCandidate) return { subject: '', content: '', previewFor: '' };
      
      previewFor = firstCandidate.candidateName;

      if (bulkEmailMode === 'template' && selectedTemplate) {
        const template = getCurrentTemplates().find(t => t.id === selectedTemplate);
        if (template) {
          subject = replaceTemplateVariables(template.subject, firstCandidate);
          content = replaceTemplateVariables(template.content, firstCandidate);
        }
      } else if (bulkEmailMode === 'custom') {
        subject = replaceTemplateVariables(bulkSubject, firstCandidate);
        content = replaceTemplateVariables(bulkMessage, firstCandidate);
      }
    } else {
      // Client preview
      const firstEmail = clientEmails.find(email => email.trim());
      if (!firstEmail) {
        return {
          subject: 'No client emails entered',
          content: 'Please enter at least one client email to preview.',
          previewFor: 'No email entered'
        };
      }
      
      previewFor = firstEmail;

      if (bulkEmailMode === 'template' && selectedTemplate) {
        const template = getCurrentTemplates().find(t => t.id === selectedTemplate);
        if (template) {
          subject = replaceTemplateVariables(template.subject);
          content = replaceTemplateVariables(template.content);
        }
      } else if (bulkEmailMode === 'custom') {
        subject = replaceTemplateVariables(bulkSubject);
        content = replaceTemplateVariables(bulkMessage);
      }
    }

    return { subject, content, previewFor };
  };

  return (
    <div className="fixed inset-0 bg-black/40 backdrop-blur-sm z-40 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-7xl h-[90vh] flex flex-col overflow-hidden">
        {/* Header - Fixed */}
        <div className="bg-gradient-to-r from-purple-600 to-pink-600 px-8 py-6 text-white flex-shrink-0">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center">
                <Mail className="w-6 h-6" />
              </div>
              <div>
                <h2 className="text-2xl font-bold">Bulk Email Center</h2>
                <p className="text-purple-100 text-sm">Send personalized emails to candidates or clients</p>
              </div>
            </div>
            <button 
              onClick={onClose}
              className="p-2 hover:bg-white/20 rounded-xl transition-colors"
            >
              <X className="w-6 h-6" />
            </button>
          </div>
        </div>

        {/* Main Content Area - Flexible */}
        <div className="flex flex-1 overflow-hidden">
          {/* Left Panel - Email Configuration */}
          <div className="w-1/2 p-8 border-r border-gray-200 overflow-y-auto">
            {/* Recipient Type Selection */}
            <div className="mb-8">
              <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                <Target className="w-5 h-5 mr-2 text-blue-600" />
                Recipient Type
              </h3>
              <div className="grid grid-cols-2 gap-3">
                <button
                  onClick={() => handleRecipientTypeChange('candidates')}
                  className={`p-4 rounded-xl border-2 transition-all text-center ${
                    recipientType === 'candidates'
                      ? 'border-blue-500 bg-blue-50 text-blue-700'
                      : 'border-gray-200 hover:border-blue-300'
                  }`}
                >
                  <Users className="w-6 h-6 mx-auto mb-2" />
                  <div className="font-medium">Candidates</div>
                  <div className="text-xs text-gray-500">{candidates.length} available</div>
                </button>
                
                <button
                  onClick={() => handleRecipientTypeChange('clients')}
                  className={`p-4 rounded-xl border-2 transition-all text-center ${
                    recipientType === 'clients'
                      ? 'border-blue-500 bg-blue-50 text-blue-700'
                      : 'border-gray-200 hover:border-blue-300'
                  }`}
                >
                  <Building2 className="w-6 h-6 mx-auto mb-2" />
                  <div className="font-medium">Clients</div>
                  <div className="text-xs text-gray-500">Manual entry</div>
                </button>
              </div>
            </div>

            {/* Email Mode Selection */}
            <div className="mb-8">
              <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                <Sparkles className="w-5 h-5 mr-2 text-purple-600" />
                Email Type
              </h3>
              <div className="grid grid-cols-2 gap-3">
                <button
                  onClick={() => setBulkEmailMode('template')}
                  className={`p-4 rounded-xl border-2 transition-all text-center ${
                    bulkEmailMode === 'template'
                      ? 'border-purple-500 bg-purple-50 text-purple-700'
                      : 'border-gray-200 hover:border-purple-300'
                  }`}
                >
                  <FileText className="w-6 h-6 mx-auto mb-2" />
                  <div className="font-medium">Templates</div>
                  <div className="text-xs text-gray-500">Pre-designed emails</div>
                </button>
                
                <button
                  onClick={() => setBulkEmailMode('custom')}
                  className={`p-4 rounded-xl border-2 transition-all text-center ${
                    bulkEmailMode === 'custom'
                      ? 'border-purple-500 bg-purple-50 text-purple-700'
                      : 'border-gray-200 hover:border-purple-300'
                  }`}
                >
                  <Edit className="w-6 h-6 mx-auto mb-2" />
                  <div className="font-medium">Custom</div>
                  <div className="text-xs text-gray-500">Create your own</div>
                </button>
              </div>
            </div>

            {/* Template Selection */}
            {bulkEmailMode === 'template' && (
              <div className="mb-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                  <FileText className="w-5 h-5 mr-2 text-green-600" />
                  Templates ({recipientType})
                </h3>
                <div className="space-y-3 max-h-80 overflow-y-auto">
                  {getCurrentTemplates().map((template) => (
                    <div
                      key={template.id}
                      className={`border rounded-xl transition-all ${
                        selectedTemplate === template.id
                          ? 'border-blue-500 bg-blue-50'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      <div 
                        onClick={() => setSelectedTemplate(template.id)}
                        className="p-4 cursor-pointer"
                      >
                        <div className="flex items-start justify-between mb-2">
                          <div className="flex items-center">
                            <span className="text-lg mr-3">{template.icon}</span>
                            <div>
                              <h4 className="font-semibold text-gray-900">{template.name}</h4>
                              <p className="text-sm text-gray-600">{template.description}</p>
                            </div>
                          </div>
                          <div className="flex items-center space-x-2">
                            <span className={`text-xs px-2 py-1 rounded-full font-medium ${
                              template.priority === 'high' 
                                ? 'bg-red-100 text-red-700' 
                                : 'bg-orange-100 text-orange-700'
                            }`}>
                              {template.priority}
                            </span>
                            <span className="text-xs bg-gray-100 px-2 py-1 rounded-full text-gray-600 font-medium">
                              {template.category}
                            </span>
                          </div>
                        </div>
                      </div>
                      
                      {/* Copy button - separate from selection area */}
                      <div className="px-4 pb-4 border-t border-gray-100 bg-gray-50 rounded-b-xl">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            copyTemplate(template);
                          }}
                          className="w-full mt-3 px-3 py-2 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors text-sm font-medium text-gray-700 flex items-center justify-center"
                        >
                          {copiedTemplate === template.id ? (
                            <>
                              <Check className="w-4 h-4 mr-2 text-green-600" />
                              Copied!
                            </>
                          ) : (
                            <>
                              <Copy className="w-4 h-4 mr-2" />
                              Copy Template
                            </>
                          )}
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Custom Email Form */}
            {bulkEmailMode === 'custom' && (
              <div className="space-y-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Email Subject
                  </label>
                  <input
                    type="text"
                    value={bulkSubject}
                    onChange={(e) => setBulkSubject(e.target.value)}
                    placeholder={`Enter email subject ${recipientType === 'candidates' ? '(use {{candidateName}}, {{client}}, {{date}})' : '(use {{date}})'}`}
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Email Message
                  </label>
                  <textarea
                    value={bulkMessage}
                    onChange={(e) => setBulkMessage(e.target.value)}
                    rows={8}
                    placeholder={`Write your email message here. ${recipientType === 'candidates' ? 'Use {{candidateName}}, {{client}}, {{date}} for personalization' : 'Use {{date}} for current date'}`}
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                  />
                </div>
                <div className="bg-blue-50 p-4 rounded-xl">
                  <p className="text-sm text-blue-800">
                    <strong>Available Variables:</strong><br/>
                    {recipientType === 'candidates' ? (
                      <>
                        • {`{{candidateName}}`} - Candidate's name<br/>
                        • {`{{client}}`} - Client name<br/>
                        • {`{{jobTitle}}`} - Job position<br/>
                        • {`{{startDate}}`} - Project start date<br/>
                        • {`{{date}}`} - Current date
                      </>
                    ) : (
                      <>
                        • {`{{date}}`} - Current date<br/>
                        • Use [Client Name] for manual replacement
                      </>
                    )}
                  </p>
                </div>
              </div>
            )}
          </div>

          {/* Right Panel - Recipient Selection */}
          <div className="w-1/2 p-8 overflow-y-auto">
            {!bulkPreviewMode ? (
              <>
                {recipientType === 'candidates' ? (
                  <>
                    {/* Candidate Selection */}
                    <div className="mb-6">
                      <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                        <Users className="w-5 h-5 mr-2 text-green-600" />
                        Select Recipients ({bulkSelectedCandidates.length} selected)
                      </h3>
                      
                      <div className="space-y-3">
                        <div className="relative">
                          <Search className="absolute left-3 top-3 h-5 w-5 text-gray-400" />
                          <input
                            type="text"
                            placeholder="Search candidates..."
                            value={bulkSearchTerm}
                            onChange={(e) => setBulkSearchTerm(e.target.value)}
                            className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-purple-500"
                          />
                        </div>
                        
                        <div className="flex items-center space-x-3">
                          <select
                            value={bulkFilterBy}
                            onChange={(e) => setBulkFilterBy(e.target.value)}
                            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                          >
                            <option value="all">All Candidates</option>
                            <option value="active">Active Only</option>
                            <option value="ending_soon">Ending Soon</option>
                            <option value="recent">Recently Added</option>
                          </select>
                          
                          <button
                            onClick={selectAllFilteredCandidates}
                            className="px-4 py-2 bg-purple-100 text-purple-800 rounded-lg hover:bg-purple-200 transition-colors text-sm font-medium"
                          >
                            Select All
                          </button>
                          
                          <button
                            onClick={clearAllSelections}
                            className="px-4 py-2 bg-gray-100 text-gray-800 rounded-lg hover:bg-gray-200 transition-colors text-sm font-medium"
                          >
                            Clear All
                          </button>
                        </div>
                      </div>
                    </div>

                    {/* Candidates List */}
                    <div className="space-y-2">
                      {getFilteredCandidatesForBulk().map((candidate) => (
                        <div
                          key={candidate.id}
                          onClick={() => toggleCandidateSelection(candidate.id)}
                          className={`p-4 rounded-xl border-2 cursor-pointer transition-all ${
                            bulkSelectedCandidates.includes(candidate.id)
                              ? 'border-purple-500 bg-purple-50'
                              : 'border-gray-200 hover:border-purple-300'
                          }`}
                        >
                          <div className="flex items-center justify-between">
                            <div className="flex items-center space-x-3">
                              <div className={`w-5 h-5 rounded border-2 flex items-center justify-center ${
                                bulkSelectedCandidates.includes(candidate.id)
                                  ? 'border-purple-500 bg-purple-500'
                                  : 'border-gray-300'
                              }`}>
                                {bulkSelectedCandidates.includes(candidate.id) && (
                                  <span className="w-3 h-3 bg-white rounded"></span>
                                )}
                              </div>
                              <div className="w-10 h-10 bg-gradient-to-r from-purple-500 to-pink-500 rounded-lg flex items-center justify-center">
                                <span className="text-white font-bold text-sm">
                                  {candidate.candidateName.split(' ').map(n => n[0]).join('')}
                                </span>
                              </div>
                              <div>
                                <div className="font-medium text-gray-900">{candidate.candidateName}</div>
                                <div className="text-sm text-gray-500">{candidate.candidateEmail}</div>
                              </div>
                            </div>
                            <div className="text-right text-sm text-gray-500">
                              <div>{candidate.client}</div>
                              <div className="text-xs">{candidate.jobTitle}</div>
                            </div>
                          </div>
                        </div>
                      ))}
                      
                      {getFilteredCandidatesForBulk().length === 0 && (
                        <div className="text-center py-8 text-gray-500">
                          <Users className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                          <p>No candidates found matching your filters</p>
                        </div>
                      )}
                    </div>
                  </>
                ) : (
                  <>
                    {/* Client Email Entry */}
                    <div className="mb-6">
                      <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                        <Building2 className="w-5 h-5 mr-2 text-blue-600" />
                        Client Email Addresses ({clientEmails.filter(e => e.trim()).length} entered)
                      </h3>
                      
                      <div className="space-y-3">
                        {clientEmails.map((email, index) => (
                          <div key={index} className="flex items-center space-x-3">
                            <div className="flex-1">
                              <input
                                type="email"
                                value={email}
                                onChange={(e) => updateClientEmail(index, e.target.value)}
                                placeholder={`Client email ${index + 1}`}
                                className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500"
                              />
                            </div>
                            {clientEmails.length > 1 && (
                              <button
                                onClick={() => removeClientEmail(index)}
                                className="p-3 text-red-600 hover:bg-red-50 rounded-xl transition-colors"
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            )}
                          </div>
                        ))}
                        
                        <button
                          onClick={addClientEmail}
                          className="w-full py-3 border-2 border-dashed border-gray-300 rounded-xl text-gray-600 hover:border-blue-300 hover:text-blue-600 transition-colors font-medium"
                        >
                          <Plus className="w-4 h-4 inline-block mr-2" />
                          Add Another Email
                        </button>
                      </div>
                      
                      <div className="mt-4 bg-orange-50 p-4 rounded-xl">
                        <p className="text-sm text-orange-800">
                          <strong>Note:</strong> When sending to clients, template placeholders like [Client Name] 
                          and candidate-specific variables will need manual replacement in the template content.
                        </p>
                      </div>
                    </div>
                  </>
                )}
              </>
            ) : (
              /* Preview Panel */
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                  <Eye className="w-5 h-5 mr-2 text-purple-600" />
                  Email Preview
                </h3>
                
                {(() => {
                  const preview = generatePreviewContent();
                  return (
                    <div className="bg-gray-50 rounded-xl p-6">
                      <div className="mb-6">
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Preview for:
                        </label>
                        <div className="bg-purple-100 p-3 rounded-lg text-purple-800 font-medium">
                          {preview.previewFor || 'No recipient selected'}
                        </div>
                      </div>
                      
                      <div className="mb-6">
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Subject Line:
                        </label>
                        <div className="bg-white p-4 rounded-lg border-l-4 border-blue-500 shadow-sm">
                          {preview.subject || 'No subject'}
                        </div>
                      </div>
                      
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Email Content:
                        </label>
                        <div className="bg-white p-6 rounded-lg border shadow-sm min-h-[300px]">
                          <div className="whitespace-pre-wrap leading-relaxed">
                            {preview.content || 'No content to preview'}
                          </div>
                        </div>
                      </div>
                      
                      <div className="mt-6 p-4 bg-blue-50 rounded-lg">
                        <p className="text-sm text-blue-800">
                          This preview shows how the email will appear for the first selected recipient. 
                          {recipientType === 'candidates' 
                            ? ' Other candidates will receive personalized versions with their specific details.'
                            : ' Other clients will receive similar content.'
                          }
                        </p>
                      </div>
                    </div>
                  );
                })()}
              </div>
            )}
          </div>
        </div>

        {/* Footer - Fixed at bottom */}
        <div className="bg-gray-50 px-8 py-4 border-t flex items-center justify-between flex-shrink-0">
          <div className="flex items-center space-x-4 text-sm text-gray-600">
            <span className="flex items-center">
              <Target className="w-4 h-4 mr-1" />
              {getRecipientCount()} recipients ({recipientType})
            </span>
            {bulkEmailMode === 'template' && selectedTemplate && (
              <span className="flex items-center">
                <FileText className="w-4 h-4 mr-1" />
                {getCurrentTemplates().find(t => t.id === selectedTemplate)?.name}
              </span>
            )}
          </div>
          
          <div className="flex items-center space-x-3">
            <button
              onClick={() => setBulkPreviewMode(!bulkPreviewMode)}
              disabled={getRecipientCount() === 0}
              className={`px-4 py-2 rounded-xl transition-colors font-medium flex items-center disabled:opacity-50 disabled:cursor-not-allowed ${
                bulkPreviewMode 
                  ? 'bg-purple-200 text-purple-800 hover:bg-purple-300' 
                  : 'bg-gray-200 text-gray-800 hover:bg-gray-300'
              }`}
            >
              <Eye className="w-4 h-4 mr-2" />
              {bulkPreviewMode ? 'Back to Selection' : 'Preview'}
            </button>
            
            <button
              onClick={sendBulkEmails}
              disabled={
  getRecipientCount() === 0 || 
  bulkSending || 
  (bulkEmailMode === 'template' && !selectedTemplate) ||
  (bulkEmailMode === 'custom' && (!bulkSubject.trim() || !bulkMessage.trim()))
}
              className="px-6 py-2 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-xl hover:from-purple-700 hover:to-pink-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed font-medium flex items-center"
            >
              {bulkSending ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  <span>Sending {Math.round(bulkProgress)}%</span>
                </>
              ) : (
                <>
                  <Send className="w-4 h-4 mr-2" />
                  <span>Send Emails ({getRecipientCount()})</span>
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BulkEmailModal;